/**
 * /data-curation — a tab bar over the curation tools.
 *
 * Hands and Datasets answer different questions off the same corpus: Hands cuts
 * dense hand-activity windows out of videos for review, Datasets ships whole
 * videos to a lab. They share filters and a search backend but nothing about how
 * a selection becomes an output, which is why this is a tab rather than a mode
 * switch inside one page. Analysis reads the same corpus but produces nothing —
 * it projects how long a collection goal takes for a slice of the fleet.
 *
 * The tab lives in the URL (?tab=datasets) so a link to one of them survives a
 * reload and can be shared.
 */
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AnalysisPage from './AnalysisPage';
import DatasetPage from './DatasetPage';
import WearerHandsPage from './WearerHandsPage';
import './DataCurationPage.css';

type Tab = 'hands' | 'datasets' | 'analysis';

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: 'hands', label: 'Hands', hint: 'Dense hand-activity windows cut out of videos' },
  { key: 'datasets', label: 'Datasets', hint: 'Whole videos, metadata and bucket delivery' },
  {
    key: 'analysis',
    label: 'Analysis',
    hint: 'Collection rates and time-to-goal projections for a slice of the fleet',
  },
];

export default function DataCurationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromUrl = new URLSearchParams(location.search).get('tab');
  // Anything unrecognised -- including the old ?tab=clips -- lands on Hands,
  // which is the default tab and what that link always meant.
  const [tab, setTab] = useState<Tab>(
    fromUrl === 'datasets' || fromUrl === 'analysis' ? fromUrl : 'hands',
  );

  const pick = (t: Tab) => {
    setTab(t);
    navigate(t === 'hands' ? '/data-curation' : `/data-curation?tab=${t}`, {
      replace: true,
    });
  };

  return (
    <div className="dc-page">
      <div className="dc-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? 'dc-tab dc-tab-on' : 'dc-tab'}
            onClick={() => pick(t.key)}
            title={t.hint}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* Both stay mounted: the Hands tab warms 42 description searches and an
          all-time video fetch on mount, and unmounting would throw that away
          every time someone looked at the other tab. */}
      <div style={{ display: tab === 'hands' ? 'block' : 'none' }}>
        <WearerHandsPage />
      </div>
      <div style={{ display: tab === 'datasets' ? 'block' : 'none' }}>
        {/* Mounted lazily on first visit, then kept: no reason to pay for the
            environment fetch until someone opens the tab. */}
        <DatasetsOnce active={tab === 'datasets'} />
      </div>
      <div style={{ display: tab === 'analysis' ? 'block' : 'none' }}>
        {/* Same lazy-then-kept deal: its all-time video fetch waits for the
            first visit, and filter/goal state survives tab switches after. */}
        <AnalysisOnce active={tab === 'analysis'} />
      </div>
    </div>
  );
}

function DatasetsOnce({ active }: { active: boolean }) {
  const [seen, setSeen] = useState(active);
  if (active && !seen) setSeen(true);
  return seen ? <DatasetPage /> : null;
}

function AnalysisOnce({ active }: { active: boolean }) {
  const [seen, setSeen] = useState(active);
  if (active && !seen) setSeen(true);
  return seen ? <AnalysisPage /> : null;
}
